import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import LaTeXRenderer from '../LaTeXRenderer';

const EditTest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    week: '',
    year: new Date().getFullYear(),
    dueDate: '',
    isActive: true,
  });
  const [problems, setProblems] = useState([
    { problemNumber: 1, title: 'Problem 1', description: '', source: '', url: '' }
  ]);
  const [pdfFile, setPdfFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchTestDetails();
  }, [id]);

  const fetchTestDetails = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/tests/${id}`);
      const test = response.data.test;
      
      setFormData({
        title: test.title,
        description: test.description || '',
        week: test.week,
        year: test.year,
        dueDate: new Date(test.dueDate).toISOString().slice(0, 16),
        isActive: test.isActive,
      });
      setProblems(test.problems);
    } catch (error) {
      setError('Failed to load test details');
      console.error('Error fetching test:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleFileChange = (e) => {
    setPdfFile(e.target.files[0]);
  };

  const handleProblemChange = (index, field, value) => {
    const updatedProblems = [...problems];
    updatedProblems[index] = {
      ...updatedProblems[index],
      [field]: field === 'maxScore' ? parseInt(value) || 0 : value,
    };
    setProblems(updatedProblems);
  };

  const addProblem = () => {
    const newProblem = {
      problemNumber: problems.length + 1,
      title: `Problem ${problems.length + 1}`,
      description: '',
      source: '',
      url: '',
    };
    setProblems([...problems, newProblem]);
  };

  const removeProblem = (index) => {
    if (problems.length > 1) {
      const updatedProblems = problems.filter((_, i) => i !== index);
      // Renumber problems
      updatedProblems.forEach((problem, i) => {
        problem.problemNumber = i + 1;
        if (!problem.title || problem.title.startsWith('Problem ')) {
          problem.title = `Problem ${i + 1}`;
        }
      });
      setProblems(updatedProblems);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });
      
      // Add problems as JSON string
      formDataToSend.append('problems', JSON.stringify(problems));
      
      // Add PDF file if selected
      if (pdfFile) {
        formDataToSend.append('pdfFile', pdfFile);
      }

      await axios.put(`${API_BASE_URL}/api/tests/${id}`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setSuccess('Test updated successfully!');
      setTimeout(() => {
        navigate('/admin');
      }, 2000);

    } catch (error) {
      setError(error.response?.data?.message || 'Failed to update test');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="container">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h1>Edit Test</h1>
          <Link to="/admin" className="btn btn-secondary">
            Back to Dashboard
          </Link>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: '24px' }}>
            {error}
          </div>
        )}

        {success && (
          <div className="alert alert-success" style={{ marginBottom: '24px' }}>
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Basic Test Information */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3>Test Information</h3>
            
            <div className="form-group">
              <label className="form-label">Test Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-label">Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="form-control"
                rows="3"
                placeholder="Brief description of the test (optional)"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div className="form-group">
                <label className="form-label">Week Number</label>
                <input
                  type="number"
                  name="week"
                  value={formData.week}
                  onChange={handleChange}
                  className="form-control"
                  min="1"
                  max="52"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Year</label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                  className="form-control"
                  min="2020"
                  max="2030"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Due Date</label>
              <input
                type="datetime-local"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>

            <div className="form-group">
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
                <span className="checkmark"></span>
                Test is active (students can see and submit)
              </label>
            </div>
          </div>

          {/* PDF File Update */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <h3>Test PDF (Optional)</h3>
            <div className="form-group">
              <label className="form-label">Replace PDF File</label>
              <input
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                className="form-control"
              />
              <div className="text-muted" style={{ fontSize: '14px', marginTop: '4px' }}>
                Only upload a new PDF if you want to replace the current one. Max 10MB.
              </div>
            </div>
          </div>

          {/* Problems Configuration */}
          <div className="card" style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h3>Problems Configuration</h3>
              <button
                type="button"
                onClick={addProblem}
                className="btn btn-success"
                style={{ fontSize: '14px' }}
              >
                Add Problem
              </button>
            </div>

            {problems.map((problem, index) => (
              <div key={index} className="card" style={{ backgroundColor: '#f8f9fa', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0 }}>Problem {problem.problemNumber}</h4>
                  {problems.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeProblem(index)}
                      className="btn btn-danger"
                      style={{ fontSize: '12px', padding: '4px 8px' }}
                    >
                      Remove
                    </button>
                  )}
                </div>

                <div className="form-group">
                  <label className="form-label">Problem Title</label>
                  <input
                    type="text"
                    value={problem.title}
                    onChange={(e) => handleProblemChange(index, 'title', e.target.value)}
                    className="form-control"
                    required
                  />
                </div>

                <div className="grid grid-2">
                  <div className="form-group">
                    <label className="form-label">Problem Source</label>
                    <input
                      type="text"
                      value={problem.source || ''}
                      onChange={(e) => handleProblemChange(index, 'source', e.target.value)}
                      className="form-control"
                      placeholder="e.g., IMO 2023, USAMO 2022"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Problem URL</label>
                    <input
                      type="url"
                      value={problem.url || ''}
                      onChange={(e) => handleProblemChange(index, 'url', e.target.value)}
                      className="form-control"
                      placeholder="e.g., https://www.imo-official.org/problems/..."
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Problem Description (LaTeX supported)</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                    <textarea
                      value={problem.description}
                      onChange={(e) => handleProblemChange(index, 'description', e.target.value)}
                      className="form-control"
                      rows="4"
                      placeholder="Problem statement… e.g. Find all $x$ such that $x^2 = 1$"
                      style={{ fontFamily: 'monospace', fontSize: '13px' }}
                    />
                    <div style={{
                      padding: '10px 12px',
                      backgroundColor: '#fff',
                      border: '1px solid #dee2e6',
                      borderRadius: '4px',
                      minHeight: '90px',
                      fontSize: '14px',
                      color: problem.description?.trim() ? '#333' : '#999',
                    }}>
                      {problem.description?.trim()
                        ? <LaTeXRenderer text={problem.description} />
                        : <em>Preview will appear here…</em>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Submit Button */}
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={submitting}
            >
              {submitting ? (
                <span>
                  <div className="spinner" style={{ 
                    width: '16px', 
                    height: '16px', 
                    display: 'inline-block', 
                    marginRight: '8px' 
                  }}></div>
                  Updating Test...
                </span>
              ) : (
                'Update Test'
              )}
            </button>
            
            <Link to="/admin" className="btn btn-secondary">
              Cancel
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditTest;
